import React, { useState, useRef } from 'react';
import { Upload, Loader2, Camera } from 'lucide-react';

interface ImageUploadDropZoneProps {
    onUpload: (file: File) => Promise<void>;
    currentImageUrl?: string | null;
    className?: string;
    variant?: 'square' | 'circle';
    size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const ImageUploadDropZone: React.FC<ImageUploadDropZoneProps> = ({
    onUpload,
    currentImageUrl,
    className = '',
    variant = 'circle',
    size = 'lg'
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        setPreviewUrl(currentImageUrl || null);
    }, [currentImageUrl]);

    const sizeClasses = {
        sm: 'w-16 h-16',
        md: 'w-24 h-24',
        lg: 'w-32 h-32',
        xl: 'w-48 h-48'
    };

    const processFile = async (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        // Show preview immediately
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);

        setIsUploading(true);
        try {
            await onUpload(file);
        } catch (error) {
            console.error('Upload error:', error);
            // Revert preview on error
            setPreviewUrl(currentImageUrl || null);
            alert('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) processFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    const handleClick = () => {
        if (!isUploading) {
            fileInputRef.current?.click();
        }
    };

    return (
        <div className={className}>
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
                className={`
                    relative group cursor-pointer transition-all
                    ${sizeClasses[size]}
                    ${variant === 'circle' ? 'rounded-full' : 'rounded-2xl'}
                    ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}
                    ${!previewUrl ? 'bg-gradient-to-br from-blue-100 to-blue-200' : ''}
                `}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*"
                    disabled={isUploading}
                />

                {/* Image or Icon */}
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt="Profile"
                        className={`w-full h-full object-cover ${variant === 'circle' ? 'rounded-full' : 'rounded-2xl'}`}
                        onError={(e) => {
                            console.error('[ImageUploadDropZone] Failed to load image:', previewUrl);
                            setPreviewUrl(null);
                        }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-12 h-12 text-blue-600" />
                    </div>
                )}

                {/* Overlay on hover or drag */}
                <div className={`
                    absolute inset-0 flex items-center justify-center transition-opacity
                    ${variant === 'circle' ? 'rounded-full' : 'rounded-2xl'}
                    ${isDragging ? 'bg-blue-500/80 opacity-100' : 'bg-black/40 opacity-0 group-hover:opacity-100'}
                `}>
                    {isUploading ? (
                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <Upload className="w-6 h-6 text-white" />
                            <span className="text-xs text-white font-medium">
                                {isDragging ? 'Drop here' : 'Upload'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
